//© 2025 Kaitlyn Troxell
import React, { useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import ManageUsersModal from "./ManageUsersModal";
import DurationModal from "./DurationModal";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import NoWorkDaysModal from './NoWorkDaysModal';
import EventListView from './EventListView';
import { useNoWorkDays, addNoWorkDay, deleteNoWorkDay, isNoWorkDay, getNoWorkDayReason } from '../hooks/useNoWorkDays';

function CalendarView({ 
  events, 
  tasks, 
  contractors, 
  saveCalendarEvent,
  updateCalendarEvent,
  setSelectedCalendarEvent,
  printCalendar,
  setShowAddUser,
  currentUser,
  isAdmin,
  projects,
  currentProject,
  onNavigate,
  onSelectProject,
  showManageUsers,
  setShowManageUsers
}) {
  const calendarRef = useRef(null);
  const lastProcessedEvent = useRef(null);
  const [durationModal, setDurationModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNoWorkModal, setShowNoWorkModal] = useState(false);
  const [mobileTasksOpen, setMobileTasksOpen] = useState(false);
  const [mobileContractorsOpen, setMobileContractorsOpen] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState('all');
  const [selectedTrade, setSelectedTrade] = useState('all');
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const { noWorkDays, loading: noWorkDaysLoading } = useNoWorkDays();

  // Convert no-work days to calendar events
  const noWorkDayEvents = React.useMemo(() => {
    const events = noWorkDays.map(nwd => {
      const dateStr = String(nwd.date);
      
      const event = {
        id: `no-work-${nwd.id}`,
        title: `🚫 ${nwd.reason || 'Holiday'}`,
        start: dateStr,
        allDay: true,
        backgroundColor: '#dc3545',
        borderColor: '#dc3545',
        color: '#dc3545',
        textColor: '#ffffff',
        editable: false,
        className: 'no-work-day-event',
        extendedProps: {
          isNoWorkDay: true,
          reason: nwd.reason
        }
      };
      
      return event;
    });
    
    return events;
  }, [noWorkDays]);

  // Combine regular events with no-work day events
  const allCalendarEvents = React.useMemo(() => {
    const combined = [...events, ...noWorkDayEvents];
    return combined;
  }, [events, noWorkDayEvents]);

  // Group tasks by phase
  const groupedTasks = React.useMemo(() => {
    const grouped = {};
    tasks.forEach(task => {
      const phase = task.phase || 'Other';
      if (!grouped[phase]) {
        grouped[phase] = [];
      }
      grouped[phase].push(task);
    });
    return grouped;
  }, [tasks]);

  // Filter tasks by selected phase and trade
  const filteredTasks = React.useMemo(() => {
    let filtered = tasks;
    
    if (selectedPhase !== 'all') {
      filtered = filtered.filter(task => (task.phase || 'Other') === selectedPhase);
    }
    
    if (selectedTrade !== 'all') {
      filtered = filtered.filter(task => (task.trade || 'Other') === selectedTrade);
    }
    
    return filtered;
  }, [tasks, selectedPhase, selectedTrade]);

  // Get unique phases and trades
  const phases = React.useMemo(() => {
    const phaseSet = new Set(tasks.map(t => t.phase || 'Other'));
    return ['all', ...Array.from(phaseSet)];
  }, [tasks]);

  const trades = React.useMemo(() => {
    const tradeSet = new Set(tasks.map(t => t.trade || 'Other'));
    return ['all', ...Array.from(tradeSet)];
  }, [tasks]);

  // Get contractor info for a task
  const getContractorForTask = (task) => {
    return task.contractor ? contractors.find(c => c.id === task.contractor) : null;
  };

  const handleDurationConfirm = (endDate) => {
    if (!durationModal) return;

    const { task, startDate, assignedContractor, taskTitle } = durationModal;

    const startET = new Date(startDate.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    startET.setHours(12, 0, 0, 0);
    
    const endDateTime = endDate instanceof Date ? new Date(endDate) : new Date(endDate);
    endDateTime.setDate(endDateTime.getDate() + 2);
    const endET = new Date(endDateTime.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    endET.setHours(12, 0, 0, 0);

    const newEvent = {
      id: Date.now().toString(),
      title: assignedContractor ? `${taskTitle} (${assignedContractor.name})` : taskTitle,
      start: startET,
      end: endET,
      allDay: true,
      backgroundColor: assignedContractor ? '#ffc107' : '#4285f4',
      borderColor: assignedContractor ? '#ffc107' : '#4285f4',
      task_id: task.id,
      contractor_id: assignedContractor ? assignedContractor.id : null
    };

    saveCalendarEvent(newEvent);
    setDurationModal(null);
  };

  const handlePDFExport = async () => {
    if (viewMode === 'list') {
      // Print list view
      window.print();
    } else {
      setLoading(true);
      
      const calendarApi = calendarRef.current.getApi();
      
      const allEvents = events;
      if (allEvents.length === 0) {
        alert("No events to print");
        setLoading(false);
        return;
      }

      let minDate = new Date(Math.min(...allEvents.map(e => e.start)));
      let maxDate = new Date(Math.max(...allEvents.map(e => e.end || e.start)));
      
      const firstMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
      const lastMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
      
      const monthsToPrint = [];
      let currentMonth = new Date(firstMonth);
      while (currentMonth <= lastMonth) {
        monthsToPrint.push(new Date(currentMonth));
        currentMonth.setMonth(currentMonth.getMonth() + 1);
      }

      const element = document.querySelector(".calendar-print-area");
      if (!element) {
        setLoading(false);
        return;
      }

      const controls = element.querySelector(".calendar-controls");
      const originalCalendar = element.querySelector('.fc');

      if (controls) controls.style.display = "none";

      const printContainer = document.createElement('div');
      printContainer.className = 'print-months-container';
      printContainer.style.display = 'none';
      document.body.appendChild(printContainer);

      for (let i = 0; i < monthsToPrint.length; i++) {
        const monthDate = monthsToPrint[i];
        
        calendarApi.gotoDate(monthDate);
        
        await new Promise(resolve => setTimeout(resolve, 1000));

        const monthWrapper = document.createElement('div');
        monthWrapper.className = 'print-page-break';
        
        const calendarContainer = document.createElement('div');
        calendarContainer.className = 'calendar-container';
        
        const calendarClone = originalCalendar.cloneNode(true);
        calendarContainer.appendChild(calendarClone);
        monthWrapper.appendChild(calendarContainer);
        
        printContainer.appendChild(monthWrapper);
      }

      printContainer.style.display = 'block';
      await new Promise(resolve => setTimeout(resolve, 100));
      printContainer.style.display = 'none';

      if (controls) controls.style.display = "flex";

      setLoading(false);

      window.print();

      setTimeout(() => {
        printContainer.remove();
      }, 1000);
    }
  };

  const getEventTitle = (task) => {
    const assignedContractor = task.contractor ? contractors.find(c => c.id === task.contractor) : null;
    return assignedContractor ? `${task.title} (${assignedContractor.name})` : task.title;
  };

  const handleEventResize = async (resizeInfo) => {
    if (!isAdmin) {
      resizeInfo.revert();
      return;
    }

    const { event } = resizeInfo;
    
    try {
      const existingEvent = events.find(e => e.id === event.id);
      
      if (existingEvent && updateCalendarEvent) {
        await updateCalendarEvent(event.id, {
          start: event.start,
          end: event.end || event.start
        });
      }
    } catch (error) {
      console.error("Error resizing event:", error);
      resizeInfo.revert();
      alert("Failed to update event duration");
    }
  };

  const handleEventDrop = async (dropInfo) => {
    if (!isAdmin) {
      dropInfo.revert();
      return;
    }

    const { event } = dropInfo;
    
    try {
      const existingEvent = events.find(e => e.id === event.id);
      
      if (existingEvent && updateCalendarEvent) {
        await updateCalendarEvent(event.id, {
          start: event.start,
          end: event.end || event.start
        });
      }
    } catch (error) {
      console.error("Error moving event:", error);
      dropInfo.revert();
      alert("Failed to move event");
    }
  };

  const handleMobileTaskClick = (task) => {
    const assignedContractor = getContractorForTask(task);
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const isOutOfOffice = task.title.toLowerCase().includes('out of office');

    const newEvent = {
      id: Date.now().toString(),
      title: assignedContractor ? `${task.title} (${assignedContractor.name})` : task.title,
      start: today,
      end: tomorrow,
      allDay: true,
      backgroundColor: isOutOfOffice ? '#dc3545' : (assignedContractor ? '#ffc107' : '#4285f4'),
      borderColor: isOutOfOffice ? '#dc3545' : (assignedContractor ? '#ffc107' : '#4285f4'),
      task_id: task.id,
      contractor_id: assignedContractor ? assignedContractor.id : null,
      className: isOutOfOffice ? 'out-of-office-event' : ''
    };

    saveCalendarEvent(newEvent);
    setMobileTasksOpen(false);
  };

  const handleEventClickForList = (event) => {
    const clickedEvent = events.find(e => e.id === event.id);
    if (clickedEvent) {
      const task = tasks.find(t => t.id === clickedEvent.task_id);
      const contractor = clickedEvent.contractor_id ? contractors.find(c => c.id === clickedEvent.contractor_id) : null;
      
      setSelectedCalendarEvent({
        ...clickedEvent,
        task: task,
        contractor: contractor
      });
    }
  };

  return (
    <>
      {/* Mobile Nav Overlay */}
      <div 
        className={`mobile-nav-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Nav Menu */}
      <div className={`mobile-nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
        <div className="mobile-nav-header">
          <h3 style={{ margin: 0 }}>Menu</h3>
          <button 
            className="mobile-nav-close"
            onClick={() => setMobileMenuOpen(false)}
          >
            ×
          </button>
        </div>
        <ul className="mobile-nav-links">
          <li>
            <button 
              className="active"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span>📅</span> Calendar
            </button>
          </li>
          {isAdmin && (
            <>
              <li>
                <button
                  onClick={() => {
                    onNavigate('punchlist');
                    setMobileMenuOpen(false);
                  }}
                >
                  <span>📋</span> Punch List
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    onNavigate('selections');
                    setMobileMenuOpen(false);
                  }}
                >
                  <span>🏠</span> Selections
                </button>
              </li>
              {/* <li>
                <button
                  onClick={() => {
                    onNavigate('swornstatements');
                    setMobileMenuOpen(false);
                  }}
                >
                  <span>📊</span> Sworn Statements
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    onNavigate('excel');
                    setMobileMenuOpen(false);
                  }}
                >
                  <span>📝</span> Line View
                </button>
              </li> */}
              <li>
                <button
                  onClick={() => {
                    onNavigate('files');
                    setMobileMenuOpen(false);
                  }}
                >
                  <span>📁</span> Files
                </button>
              </li>
            </>
          )}
        </ul>
      </div>

      {/* Mobile FAB Buttons - Only for Admin */}
      <div className="mobile-fab-container" style={{ display: isAdmin ? 'flex' : 'none' }}>
        <button 
          className="mobile-fab tasks"
          onClick={() => setMobileTasksOpen(true)}
          title="Tasks"
        >
          📋
          {tasks.length > 0 && (
            <span className="mobile-fab-badge">{tasks.length}</span>
          )}
        </button>
        <button 
          className="mobile-fab contractors"
          onClick={() => setMobileContractorsOpen(true)}
          title="Contractors"
        >
          👷
          {contractors.length > 0 && (
            <span className="mobile-fab-badge">{contractors.length}</span>
          )}
        </button>
        <button 
          className="mobile-fab manage-users"
          onClick={() => setShowManageUsers(true)}
          title="Manage Users"
        >
          👥
        </button>
      </div>

      {/* Mobile Tasks Bottom Sheet */}
      <div 
        className={`mobile-sheet-overlay ${mobileTasksOpen ? 'active' : ''}`}
        onClick={() => setMobileTasksOpen(false)}
      />
      <div className={`mobile-bottom-sheet ${mobileTasksOpen ? 'active' : ''}`}>
        <div className="mobile-sheet-handle"></div>
        <div className="mobile-sheet-header">
          <h3 className="mobile-sheet-title">📋 Tasks</h3>
          <button 
            className="mobile-sheet-close"
            onClick={() => setMobileTasksOpen(false)}
          >
            ×
          </button>
        </div>
        <div className="mobile-sheet-content">
          <div className="phase-filter" style={{ marginBottom: '1rem' }}>
            <label className="filter-label">Filter by Phase:</label>
            <select 
              className="filter-select" 
              value={selectedPhase}
              onChange={(e) => setSelectedPhase(e.target.value)}
            >
              <option value="all">All Phases</option>
              {phases.filter(p => p !== 'all').map(phase => (
                <option key={phase} value={phase}>{phase}</option>
              ))}
            </select>
          </div>

          <div className="phase-filter" style={{ marginBottom: '1rem' }}>
            <label className="filter-label">Filter by Trade:</label>
            <select 
              className="filter-select"
              value={selectedTrade}
              onChange={(e) => setSelectedTrade(e.target.value)}
            >
              <option value="all">All Trades</option>
              {trades.filter(t => t !== 'all').map(trade => (
                <option key={trade} value={trade}>{trade}</option>
              ))}
            </select>
          </div>

          {filteredTasks.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
              No tasks available. Drag tasks from desktop view.
            </p>
          ) : (
            filteredTasks.map(task => {
              const contractor = getContractorForTask(task);
              const isCompleted = task.completed;
              const isOutOfOffice = task.title.toLowerCase().includes('out of office');
              
              return (
                <div
                  key={task.id}
                  className={`task-item ${isCompleted ? 'completed' : contractor ? 'assigned' : 'pending'} ${isOutOfOffice ? 'out-of-office' : ''}`}
                  style={{ marginBottom: '0.75rem', cursor: 'pointer' }}
                  onClick={() => handleMobileTaskClick(task)}
                >
                  <div className="task-title">
                    {task.title} {isCompleted && '✓'}
                  </div>
                  {contractor ? (
                    <div className="contractor-assigned">
                      👷 {contractor.name}
                    </div>
                  ) : (
                    <div className="contractor-unassigned">
                      No contractor assigned
                    </div>
                  )}
                  <div style={{ 
                    fontSize: '10px', 
                    padding: '4px 6px', 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontStyle: 'italic'
                  }}>
                    Tap to add to today
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Mobile Contractors Bottom Sheet */}
      <div 
        className={`mobile-sheet-overlay ${mobileContractorsOpen ? 'active' : ''}`}
        onClick={() => setMobileContractorsOpen(false)}
      />
      <div className={`mobile-bottom-sheet ${mobileContractorsOpen ? 'active' : ''}`}>
        <div className="mobile-sheet-handle"></div>
        <div className="mobile-sheet-header">
          <h3 className="mobile-sheet-title">👷 Contractors</h3>
          <button 
            className="mobile-sheet-close"
            onClick={() => setMobileContractorsOpen(false)}
          >
            ×
          </button>
        </div>
        <div className="mobile-sheet-content">
          {contractors.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
              No contractors available.
            </p>
          ) : (
            contractors.map(contractor => (
              <div
                key={contractor.id}
                className={`contractor-card ${contractor.preferred ? 'preferred' : contractor.status === 'active' ? 'active' : 'inactive'}`}
                style={{ marginBottom: '0.75rem' }}
              >
                <div className="contractor-name">{contractor.name}</div>
                {contractor.trade && (
                  <div className="contractor-phone">Trade: {contractor.trade}</div>
                )}
                {contractor.phone && (
                  <div className="contractor-phone">📞 {contractor.phone}</div>
                )}
                {contractor.email && (
                  <div className="contractor-email">✉️ {contractor.email}</div>
                )}
                {contractor.rating && (
                  <div className="contractor-rating">⭐ {contractor.rating}/5</div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <div className="calendar-container">
        {/* Mobile Header with Hamburger Menu and Project Selector */}
        {isAdmin && (
          <div className="calendar-mobile-header">
            <button 
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(true)}
            >
              ☰
            </button>
            <h1 style={{ margin: 0, fontSize: '18px', color: '#333', flex: 1 }}>
              📅 Calendar
            </h1>
            <select
              className="mobile-project-selector"
              value={currentProject?.id || ''}
              onChange={(e) => {
                if (onSelectProject && e.target.value) {
                  onSelectProject(e.target.value);
                }
              }}
            >
              <option value="">Select Project</option>
              {projects && projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="calendar-print-area">
          {isAdmin && (
            <div className={`print-hide calendar-controls ${showManageUsers ? 'modal-open' : ''}`}>
              {onNavigate && (
                <>
                  <button onClick={() => onNavigate('punchlist')} className="screenshot-btn" style={{ backgroundColor: '#ffc107', borderColor: '#ffc107' }}>
                    Punch List
                  </button>
                  <button onClick={() => onNavigate('selections')} className="screenshot-btn" style={{ backgroundColor: '#007bff', borderColor: '#007bff' }}>
                    Selections
                  </button>
                  {/* <button 
                    onClick={() => onNavigate('swornstatements')}
                    className="screenshot-btn"
                    style={{ padding: '8px 16px', marginLeft: '10px', backgroundColor: '#17a2b8', borderColor: '#17a2b8' }}
                  >
                    Sworn Statements
                  </button>
                  <button 
                    onClick={() => onNavigate('excel')}
                    className="screenshot-btn"
                    style={{ backgroundColor: '#56c5e4ff', borderColor: '#56c5e4ff' }}
                  >
                    Line View
                  </button> */}
                  <button 
                    onClick={() => onNavigate('files')}
                    className="screenshot-btn"
                    style={{ padding: '8px 16px', marginLeft: '10px', backgroundColor: '#d78c3d', borderColor: '#d78c3d' }}
                  >
                    Files
                  </button>
                </>
              )}
              {viewMode === 'calendar' ? (
                <button onClick={handlePDFExport} className="screenshot-btn" style={{ backgroundColor: '#074818ff', borderColor: '#074818ff' }}>
                  Print Calendar
                </button>
              ) : (
                <button onClick={handlePDFExport} className="screenshot-btn" style={{ backgroundColor: '#074818ff', borderColor: '#074818ff' }}>
                  Print List
                </button>
              )}
              {setShowAddUser && (
                <button onClick={() => setShowManageUsers(true)} className="manage-users-btn" style={{ backgroundColor: '#ae00ffff', borderColor: '#ae00ffff' }}>
                  Manage Users
                </button>
              )}
            </div>
          )}

          {!isAdmin && (
            <div className="print-hide calendar-controls" style={{ justifyContent: 'center' }}>
              <button onClick={handlePDFExport} className="screenshot-btn" style={{ backgroundColor: '#007bff', borderColor: '#007bff' }}>Print Calendar</button>
            </div>
          )}

          {isAdmin && (
            <div className="print-hide calendar-controls" style={{ justifyContent: 'center' }}>
              <button
                  onClick={() => setShowNoWorkModal(true)}
                  className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center gap-1"
                  title="Manage no-work days"
                >
                  No Work
                </button>
                <button 
                  onClick={() => setViewMode(viewMode === 'calendar' ? 'list' : 'calendar')} 
                  className="view-toggle-btn"
                  style={{ backgroundColor: '#6c757d', borderColor: '#6c757d' }}
                >
                  {viewMode === 'calendar' ? '📋 List View' : '📅 Calendar View'}
                </button>
            </div>
          )}

          <div className="calendar-print-header">
            <h2>{currentProject?.name || 'Tentative Construction Schedule'}</h2>
          </div>
          
          {/* Conditional rendering based on viewMode */}
          {viewMode === 'list' ? (
            <EventListView
              events={events}
              contractors={contractors}
              tasks={tasks}
              onEventClick={handleEventClickForList}
              isAdmin={isAdmin}
            />
          ) : (
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              editable={isAdmin}
              droppable={isAdmin}
              selectable={isAdmin}
              selectMirror={isAdmin}
              events={allCalendarEvents}
              eventDisplay="block"
              dayMaxEvents={false}
              height="auto"
              eventMaxStack={false}
              displayEventTime={false}
              displayEventEnd={true}
              eventResizableFromStart={isAdmin}
              eventDurationEditable={isAdmin}

              eventResize={isAdmin ? handleEventResize : undefined}
              eventDrop={isAdmin ? handleEventDrop : undefined}

              select={isAdmin ? (selectionInfo) => {
                const newEvent = {
                  id: Date.now().toString(),
                  title: "New Event",
                  start: selectionInfo.start,
                  end: selectionInfo.end,
                  allDay: selectionInfo.allDay
                };
                saveCalendarEvent(newEvent);
              } : undefined}

              eventReceive={isAdmin ? (info) => {
                const eventKey = `${info.draggedEl.textContent}_${info.event.start.getTime()}`;
                
                if (lastProcessedEvent.current === eventKey) {
                  info.event.remove();
                  return;
                }
                
                lastProcessedEvent.current = eventKey;
                setTimeout(() => { lastProcessedEvent.current = null; }, 1000);
                info.event.remove();
                
                const fcEventElement = info.draggedEl.classList.contains('fc-event') 
                  ? info.draggedEl 
                  : info.draggedEl.querySelector('.fc-event');
                if (!fcEventElement) return;

                const taskTitle = fcEventElement.textContent.replace(' ✓', '').trim();
                const draggedTask = tasks.find(task => task.title === taskTitle);
                if (!draggedTask) return;

                const assignedContractor = draggedTask.contractor ? contractors.find(c => c.id === draggedTask.contractor) : null;

                const startET = new Date(info.event.start.toLocaleString('en-US', { timeZone: 'America/New_York' }));
                startET.setHours(12, 0, 0, 0);
                
                const endET = new Date(startET);
                endET.setDate(endET.getDate() + 1);

                const isOutOfOffice = taskTitle.toLowerCase().includes('out of office');

                const newEvent = {
                  id: Date.now().toString(),
                  title: assignedContractor ? `${taskTitle} (${assignedContractor.name})` : taskTitle,
                  start: startET,
                  end: endET,
                  allDay: true,
                  backgroundColor: isOutOfOffice ? '#dc3545' : (assignedContractor ? '#ffc107' : '#4285f4'),
                  borderColor: isOutOfOffice ? '#dc3545' : (assignedContractor ? '#ffc107' : '#4285f4'),
                  task_id: draggedTask.id,
                  contractor_id: assignedContractor ? assignedContractor.id : null,
                  className: isOutOfOffice ? 'out-of-office-event' : ''
                };

                saveCalendarEvent(newEvent);
              } : undefined}
              
              eventClick={isAdmin ? async (info) => {
                const clickedEvent = events.find(event => event.id === info.event.id);
                if (clickedEvent) {
                  let task = tasks.find(t => t.id === clickedEvent.task_id);
                  
                  if (task && clickedEvent.task_id) {
                    try {
                      const { doc, getDoc } = await import('firebase/firestore');
                      const { db } = await import('../firebase');
                      const taskDoc = await getDoc(doc(db, 'tasks', clickedEvent.task_id));
                      if (taskDoc.exists()) {
                        task = { id: taskDoc.id, ...taskDoc.data() };
                      }
                    } catch (error) {
                      console.error('Error fetching task details:', error);
                    }
                  }
                  
                  const contractor = clickedEvent.contractor_id ? contractors.find(c => c.id === clickedEvent.contractor_id) : null;
                  
                  setSelectedCalendarEvent({
                    ...clickedEvent,
                    task: task,
                    contractor: contractor
                  });
                }
              } : undefined}

              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,dayGridWeek'
              }}
            />
          )}
          
          {isAdmin && showManageUsers && (
            <ManageUsersModal 
              setShowManageUsers={setShowManageUsers}
              currentUser={currentUser}
              projects={projects}
            />
          )}
          {durationModal && (
            <DurationModal
              task={durationModal.task}
              startDate={durationModal.startDate}
              assignedContractor={durationModal.assignedContractor}
              onConfirm={handleDurationConfirm}
              onCancel={() => setDurationModal(null)}
            />
          )}
          {showNoWorkModal && (
            <NoWorkDaysModal
              noWorkDays={noWorkDays}
              onClose={() => setShowNoWorkModal(false)}
              onAddNoWorkDay={addNoWorkDay}
              onDeleteNoWorkDay={deleteNoWorkDay}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default CalendarView;